import React, { lazy, useRef, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import DropDown from "./DropDown";
import { setDropdown } from "../../../redux/utils/dropdownActions";
import { useUserRoles } from "../../../authentication/authentication";

import { motion, AnimatePresence } from "framer-motion";
import {
  Accessibility,
  Banknote,
  BanknoteArrowDown,
  BarChart3,
  Calendar1,
  CalendarPlus2,
  ClipboardList,
  FileHeart,
  FolderHeart,
  Heart,
  HeartPlus,
  Hospital,
  LayoutDashboard,
  Package,
  Pill,
  Settings,
  Syringe,
  User,
  UserPlus,
  Users,
} from "lucide-react";
import { arClass } from "../../../utils/useArClass";
import logos from "../../../images/logos";
import { isAdminRole } from "../../../utils/adminChecker";

const Txt = lazy(() => import("../../Text"));

// Custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.5);
    border-radius: 10px;
    transition: all 0.3s ease;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(148, 163, 184, 0.8);
  }
  
  .custom-scrollbar-dark::-webkit-scrollbar {
    width: 8px;
  }
  
  .custom-scrollbar-dark::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .custom-scrollbar-dark::-webkit-scrollbar-thumb {
    background: rgba(71, 85, 105, 0.5);
    border-radius: 10px;
    transition: all 0.3s ease;
  }
  
  .custom-scrollbar-dark::-webkit-scrollbar-thumb:hover {
    background: rgba(71, 85, 105, 0.8);
  }
`;

/**
 * Text component for the Dashboard page for translations
 * @param children - The text content to be translated
 * @param className - Optional CSS class names
 * @return Translated text element
 */
const Text = ({ children, className }) => {
  return (
    <Txt page="NAVIGATION" className={className}>
      {children}
    </Txt>
  );
};

// Enhanced Motion-wrapped NavLink with spring physics
const MotionNavLink = motion(NavLink);

// Icon animation variants
const iconVariants = {
  normal: { scale: 1, rotate: 0 },
  hover: {
    scale: 1.1,
    rotate: 5,
    transition: { type: "spring", stiffness: 400, damping: 10 },
  },
};

const dropdownLinks = {
  Patients: {
    mainName: "Patients",
    items: [
      {
        name: "View All Patients",
        url: "/patients",
        icon: <Accessibility  />,
        method: "get",
      },
      {
        name: "Add Patient",
        url: "/create-patient",
        icon: <UserPlus  />,
        method: "post",
      },
    ],
    accessKeys: ["getPatient", "postPatient"],
  },

  Appointments: {
    mainName: "Appointments",
    items: [
      {
        name: "View All Appointments",
        url: "/appointments",
        icon: <Calendar1 />,
        method: "get",
      },
      {
        name: "Add Appointment",
        url: "/create-appointment",
        icon: <CalendarPlus2 />,
        method: "post",
      },
    ],
    accessKeys: ["getAppointment", "postAppointment"],
  },

  Billings: {
    mainName: "Billings",
    items: [
      {
        name: "View All Billings",
        url: "/billings",
        icon: <Banknote />,
        method: "get",
      },
      {
        name: "Add Billing",
        url: "/create-billing",
        icon: <BanknoteArrowDown />,
        method: "post",
      },
    ],
    accessKeys: ["getBilling", "postBilling"],
  },
};

export default function AdminSideNav() {
  const roles = useUserRoles();
  const dispatch = useDispatch();
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );
  const darkMode = useSelector((state) => state.settings.darkMode);
  const sidebarStyle = useSelector((state) => state.settings.sidebarStyle);
  const isDoctorRole = roles ? roles.includes("doctor") : false;
  // Refs for measuring height
  const navRef = useRef(null);
  const logoRef = useRef(null);

  // Calculate available height for dropdown section
  useEffect(() => {
    // Inject custom scrollbar styles
    const styleTag = document.createElement("style");
    styleTag.textContent = scrollbarStyles;
    document.head.appendChild(styleTag);

    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  let singleText = {};

  if (defaultLanguage === "fr") {
    singleText = {
      dashboard: "Tableau de bord",
      myProfile: "Mon profil",
      waitingRoom: "Salle d'attente",
      reception: "Reception",
      myVisits: "Mes visites",
      clinicVisits: "Visites de la clinique",
      myTriages: "Mes triages",
      clinicTriages: "Triages de la clinique",
      statistics: "Statistiques",
      team: "Équipe",
      settings: "Paramètres",
      toolsAndEquipment: "Outils et équipements",
      materials: "Matériaux",
      medicaments: "Médicaments",
    };
  } else if (defaultLanguage === "ar") {
    singleText = {
      dashboard: "لوحة القيادة",
      myProfile: "ملفي الشخصي",
      waitingRoom: "غرفة الانتظار",
      reception: "الاستقبال",
      myVisits: "زياراتي",
      clinicVisits: "زيارات العيادة",
      myTriages: "فحوصاتي",
      clinicTriages: "فحوصات العيادة",
      statistics: "إحصائيات",
      team: "الفريق",
      settings: "الإعدادات",
      toolsAndEquipment: "الأدوات والمعدات",
      materials: "المواد",
      medicaments: "الأدوية",
    };
  } else {
    singleText = {
      dashboard: "Dashboard",
      myProfile: "My Profile",
      waitingRoom: "Waiting Room",
      reception: "Reception",
      myVisits: "My Visits",
      clinicVisits: "Clinic Visits",
      myTriages: "My Triages",
      clinicTriages: "Clinic Triages",
      statistics: "Statistics",
      team: "Team",
      settings: "Settings",
      toolsAndEquipment: "Tools & Equipment",
      materials: "Materials",
      medicaments: "Medicaments",
    };
  }
  const hideMdDropdownVisibility = () => {
    dispatch(setDropdown(null));
  };

  const dashColors = darkMode
    ? {
        bg: "bg-slate-700/30 text-slate-100",
        color: "text-slate-100",
        border: "border-slate-200",
        hover: "hover:bg-slate-800/20",
        active: "bg-slate-700 text-slate-50 border-slate-400 shadow-lg",
        iconBg: "bg-slate-700",
        glow: "shadow-lg shadow-slate-900/20",
      }
    : {
        bg: "bg-slate-200 text-slate-900",
        color: "text-slate-900",

        border: "border-slate-300",
        hover: "hover:bg-slate-300",
        active: "bg-slate-200 text-slate-900 border-slate-400 shadow-lg",
        iconBg: "bg-slate-500",
        glow: "shadow-lg shadow-slate-500/20",
      };

  const getColorScheme = (index) => {
    const schemes = [
      {
        light: {
          bg: "bg-violet-300 text-violet-900",
          color: "!text-violet-900",
          subBg: "bg-violet-200/90 backdrop-blur-sm",
          border: "border-violet-400",
          hover: "hover:bg-violet-400",
          active: "bg-violet-400 text-violet-900 border-violet-500 shadow-lg",
          iconBg: "bg-violet-700",
          glow: "shadow-lg shadow-violet-500/20",
        },
        dark: {
          bg: "bg-violet-700/30 text-violet-50",
          color: "!text-violet-50",
          subBg: "bg-violet-100/30 backdrop-blur-sm",
          border: "border-violet-200",
          hover: "hover:bg-violet-600/32",
          active: "bg-violet-500 text-white border-violet-200 shadow-lg",
          iconBg: "bg-violet-600",
          glow: "shadow-lg shadow-violet-600/22",
        },
      },
      {
        light: {
          bg: "bg-emerald-300 text-emerald-900",
          color: "text-emerald-900",
          subBg: "bg-emerald-200/90 backdrop-blur-sm",
          border: "border-emerald-400",
          hover: "hover:bg-emerald-400",
          active:
            "bg-emerald-400 text-emerald-900 border-emerald-500 shadow-lg",
          iconBg: "bg-emerald-700",
          glow: "shadow-lg shadow-emerald-500/18",
        },
        dark: {
          bg: "bg-emerald-700/30 text-emerald-50",
          color: "text-emerald-50",
          subBg: "bg-emerald-100/30 backdrop-blur-sm",
          border: "border-emerald-200",
          hover: "hover:bg-emerald-600/32",
          active: "bg-emerald-500 text-white border-emerald-200 shadow-lg",
          iconBg: "bg-emerald-600",
          glow: "shadow-lg shadow-emerald-600/20",
        },
      },
      {
        light: {
          bg: "bg-amber-300 text-amber-900",
          color: "text-amber-900",
          subBg: "bg-amber-200/90 backdrop-blur-sm",
          border: "border-amber-400",
          hover: "hover:bg-amber-400",
          active: "bg-amber-400 text-amber-900 border-amber-500 shadow-lg",
          iconBg: "bg-amber-700",
          glow: "shadow-lg shadow-amber-500/18",
        },
        dark: {
          bg: "bg-amber-700/30 text-amber-50",
          color: "text-amber-50",
          subBg: "bg-amber-100/30 backdrop-blur-sm",
          border: "border-amber-200",
          hover: "hover:bg-amber-600/32",
          active: "bg-amber-500 text-white border-amber-200 shadow-lg",
          iconBg: "bg-amber-600",
          glow: "shadow-lg shadow-amber-600/20",
        },
      },
      {
        light: {
          bg: "bg-indigo-300 text-indigo-900",
          color: "text-indigo-900",
          subBg: "bg-indigo-200/90 backdrop-blur-sm",
          border: "border-indigo-400",
          hover: "hover:bg-indigo-400",
          active: "bg-indigo-400 text-indigo-900 border-indigo-500 shadow-lg",
          iconBg: "bg-indigo-700",
          glow: "shadow-lg shadow-indigo-500/18",
        },
        dark: {
          bg: "bg-indigo-700/30 text-indigo-50",
          color: "text-indigo-50",
          subBg: "bg-indigo-100/30 backdrop-blur-sm",
          border: "border-indigo-200",
          hover: "hover:bg-indigo-600/32",
          active: "bg-indigo-500 text-white border-indigo-200 shadow-lg",
          iconBg: "bg-indigo-600",
          glow: "shadow-lg shadow-indigo-600/20",
        },
      },
      {
        light: {
          bg: "bg-cyan-300 text-cyan-900",
          color: "text-cyan-900",
          subBg: "bg-cyan-200/90 backdrop-blur-sm",
          border: "border-cyan-400",
          hover: "hover:bg-cyan-400",
          active: "bg-cyan-400 text-cyan-900 border-cyan-500 shadow-lg",
          iconBg: "bg-cyan-700",
          glow: "shadow-lg shadow-cyan-500/18",
        },
        dark: {
          bg: "bg-cyan-700/30 text-cyan-50",
          color: "text-cyan-50",
          subBg: "bg-cyan-100/30 backdrop-blur-sm",
          border: "border-cyan-200",
          hover: "hover:bg-cyan-600/32",
          active: "bg-cyan-500 text-white border-cyan-200 shadow-lg",
          iconBg: "bg-cyan-600",
          glow: "shadow-lg shadow-cyan-600/20",
        },
      },
      {
        light: {
          bg: "bg-rose-300 text-rose-900",
          color: "text-rose-900",
          subBg: "bg-rose-200/90 backdrop-blur-sm",
          border: "border-rose-400",
          hover: "hover:bg-rose-400",
          active: "bg-rose-400 text-rose-900 border-rose-500 shadow-lg",
          iconBg: "bg-rose-700",
          glow: "shadow-lg shadow-rose-500/18",
        },
        dark: {
          bg: "bg-rose-700/30 text-rose-50",
          color: "text-rose-50",
          subBg: "bg-rose-100/30 backdrop-blur-sm",
          border: "border-rose-200",
          hover: "hover:bg-rose-600/32",
          active: "bg-rose-500 text-white border-rose-200 shadow-lg",
          iconBg: "bg-rose-600",
          glow: "shadow-lg shadow-rose-600/20",
        },
      },
      {
        light: {
          bg: "bg-teal-300 text-teal-900",
          color: "text-teal-900",
          subBg: "bg-teal-200/90 backdrop-blur-sm",
          border: "border-teal-400",
          hover: "hover:bg-teal-400",
          active: "bg-teal-400 text-teal-900 border-teal-500 shadow-lg",
          iconBg: "bg-teal-700",
          glow: "shadow-lg shadow-teal-500/18",
        },
        dark: {
          bg: "bg-teal-700/30 text-teal-50",
          color: "text-teal-50",
          subBg: "bg-teal-100/30 backdrop-blur-sm",
          border: "border-teal-200",
          hover: "hover:bg-teal-600/32",
          active: "bg-teal-500 text-white border-teal-200 shadow-lg",
          iconBg: "bg-teal-600",
          glow: "shadow-lg shadow-teal-600/20",
        },
      },
      {
        light: {
          bg: "bg-orange-300 text-orange-900",
          color: "text-orange-900",
          subBg: "bg-orange-200/90 backdrop-blur-sm",
          border: "border-orange-400",
          hover: "hover:bg-orange-400",
          active: "bg-orange-400 text-orange-900 border-orange-500 shadow-lg",
          iconBg: "bg-orange-700",
          glow: "shadow-lg shadow-orange-500/16",
        },
        dark: {
          bg: "bg-orange-700/30 text-orange-50",
          color: "text-orange-50",
          subBg: "bg-orange-100/30 backdrop-blur-sm",
          border: "border-orange-200",
          hover: "hover:bg-orange-600/30",
          active: "bg-orange-500 text-white border-orange-200 shadow-lg",
          iconBg: "bg-orange-600",
          glow: "shadow-lg shadow-orange-600/20",
        },
      },
      {
        light: {
          bg: "bg-green-300 text-green-900",
          color: "text-green-900",
          subBg: "bg-green-200/90 backdrop-blur-sm",
          border: "border-green-400",
          hover: "hover:bg-green-400",
          active: "bg-green-400 text-green-900 border-green-500 shadow-lg",
          iconBg: "bg-green-700",
          glow: "shadow-lg shadow-green-500/18",
        },
        dark: {
          bg: "bg-green-700/30 text-green-50",
          color: "text-green-50",
          subBg: "bg-green-100/30 backdrop-blur-sm",
          border: "border-green-200",
          hover: "hover:bg-green-600/32",
          active: "bg-green-500 text-white border-green-200 shadow-lg",
          iconBg: "bg-green-600",
          glow: "shadow-lg shadow-green-600/20",
        },
      },
      {
        light: {
          bg: "bg-blue-300 text-blue-900",
          color: "text-blue-900",
          subBg: "bg-blue-200/90 backdrop-blur-sm",
          border: "border-blue-400",
          hover: "hover:bg-blue-400",
          active: "bg-blue-400 text-blue-900 border-blue-500 shadow-lg",
          iconBg: "bg-blue-700",
          glow: "shadow-lg shadow-blue-500/18",
        },
        dark: {
          bg: "bg-blue-700/30 text-blue-50",
          color: "text-blue-50",
          subBg: "bg-blue-100/30 backdrop-blur-sm",
          border: "border-blue-200",
          hover: "hover:bg-blue-600/32",
          active: "bg-blue-500 text-white border-blue-200 shadow-lg",
          iconBg: "bg-blue-600",
          glow: "shadow-lg shadow-blue-600/20",
        },
      },
      {
        light: {
          bg: "bg-gray-300 text-gray-900",
          color: "text-gray-900",
          subBg: "bg-gray-200/90 backdrop-blur-sm",
          border: "border-gray-400",
          hover: "hover:bg-gray-400",
          active: "bg-gray-400 text-gray-900 border-gray-500 shadow-lg",
          iconBg: "bg-gray-700",
          glow: "shadow-lg shadow-gray-500/18",
        },
        dark: {
          bg: "bg-gray-700/30 text-gray-50",
          color: "text-gray-50",
          subBg: "bg-gray-100/30 backdrop-blur-sm",
          border: "border-gray-200",
          hover: "hover:bg-gray-600/32",
          active: "bg-gray-500 text-white border-gray-200 shadow-lg",
          iconBg: "bg-gray-600",
          glow: "shadow-lg shadow-gray-600/20",
        },
      },
    ];

    const entry = schemes[index % schemes.length];
    return darkMode ? entry.dark : entry.light;
  };

  // helper to adapt classes to sidebarStyle
  const applySidebarStyle = (colors) => {
    if (
      sidebarStyle === "default" ||
      !sidebarStyle ||
      sidebarStyle ===
        "no-bg-no-icon-colors-no-text-colors-no-dropdown-colors-no-border"
    ) {
      const textColor = darkMode ? "text-white" : "text-black";
      const iconColor = darkMode ? "text-white" : "text-black";
      const hoverBg = darkMode
        ? "hover:bg-gray-600/20"
        : "hover:bg-gray-300/70";
      const activeBg = darkMode ? "bg-gray-700/40" : "bg-gray-300/70";
      const subBg = darkMode ? "bg-gray-900/20" : "bg-gray-100/50";

      return {
        containerActive: `${activeBg} ${textColor} shadow-none border-0`,
        containerInactive: `bg-transparent ${textColor} ${hoverBg} shadow-none border-0`,
        subBg: subBg,
        iconBg: "bg-transparent",
        iconText: iconColor,
        textColor: textColor,
      };
    }

    if (sidebarStyle === "neon") {
      return {
        containerActive: `${colors.active} ${colors.border} ${colors.glow} border border-l-[5px]`,
        containerInactive: `${colors.bg} ${colors.border} ${colors.hover} border hover:shadow-lg`,
        subBg: colors.subBg,
        iconBg: colors.iconBg,
        iconText: "text-white",
        textColor: colors.color,
      };
    }

    if (sidebarStyle === "no-bg-no-border") {
      return {
        containerActive: `${colors.active} ${colors.border} ${colors.glow} shadow-none !border-0`,
        containerInactive: `bg-transparent ${colors.hover} text-gray-800 dark:text-gray-200 hover:shadow-none`,
        subBg: `${colors.subBg} border-l-[5px] shadow-none !border-t-0 !border-b-0 !border-r-0`,
        iconBg: colors.iconBg,
        iconText: "text-white",
        textColor: colors.color,
      };
    }

    if (sidebarStyle === "bg-no-border") {
      return {
        containerActive: `${colors.active} ${colors.glow} `,
        containerInactive: `${colors.bg} ${colors.hover} hover:shadow-lg`,
        subBg: `${colors.subBg} border-l-[5px] shadow-none !border-t-0 !border-b-0 !border-r-0`,
        iconBg: colors.iconBg,
        iconText: "text-white",
        textColor: colors.color,
      };
    }

    return {
      containerActive: `${colors.active} ${colors.border} ${colors.glow} border border-l-[5px]`,
      containerInactive: `${colors.bg} ${colors.border} ${colors.hover}`,
      subBg: colors.subBg,
      iconBg: colors.iconBg,
      iconText: "text-white",
      textColor: colors.color,
    };
  };
  const dashColorsRaw = dashColors; // existing
  const dashStyled = applySidebarStyle(dashColorsRaw);

  const getDashLabelColor = () => {
    if (
      sidebarStyle === "default" ||
      !sidebarStyle ||
      sidebarStyle ===
        "no-bg-no-icon-colors-no-text-colors-no-dropdown-colors-no-border"
    ) {
      return darkMode ? "text-white" : "text-black";
    }
    return dashColorsRaw.color;
  };

  const shouldShowShadow = () => {
    return sidebarStyle === "neon";
  };

  return (
    <motion.nav
      ref={navRef}
      className={`admin-side-nav w-60 max-xl:w-20 min-h-lvh max-sm:hidden ${
        darkMode
          ? "dashboard-secondary-color-dark"
          : "dashboard-secondary-color"
      }
      
      `}
    >
      {/* Logo with animation */}
      <motion.div
        ref={logoRef}
        className={`flex max-xl:justify-center   mt-1 fixed  top-0 z-10 bg-inherit rounded-lg ${defaultLanguage !== "ar" ? "left-0" : "right-0"}`}
    
        transition={{ type: "spring", stiffness: 400, damping: 17 }}
      >
        <NavLink to="/dashboard" className={arClass("")}>
          <motion.img
            className={arClass("w-[210px] px-1  max-xl:hidden !bg-transparent")}
            src={darkMode ? logos.lightTextLogo : logos.darkTextLogo}
            alt="logo"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300, damping: 10 }}
          />
          <motion.img
            className={arClass("w-[50px]  p-1 hidden max-xl:flex !bg-transparent")}
            src={darkMode ? logos.lightLogo : logos.darkLogo}
            alt="logo"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300, damping: 10 }}
          />
        </NavLink>
      </motion.div>

      <div
        className={arClass(
          `nav-content flex flex-col gap-3 p-2 mx-3 mt-1 max-xl:mx-0 max-xl:items-center overflow-y-auto  overflow-x-hidden`,
        )}
        style={{ height: `calc(100vh` }}
      >
        <div className={arClass("flex flex-col gap-3 px-1 mt-24")}>
          {/* Dashboard Link */}
          <MotionNavLink
            onClick={hideMdDropdownVisibility}
            className={({ isActive }) =>
              arClass(
                `flex items-center gap-3 p-2 rounded-xl text-sm font-medium w-full transition-all duration-300 ${
                  isActive
                    ? dashStyled.containerActive
                    : dashStyled.containerInactive
                }`,
              )
            }
            to="/dashboard"
          >
            <motion.div
              className={arClass(
                `w-8 h-8 rounded-lg flex items-center justify-center ${
                  dashStyled.iconBg
                } ${shouldShowShadow() ? "shadow-lg" : ""}`,
              )}
              variants={iconVariants}
              whileHover="hover"
            >
              <LayoutDashboard className={`${dashStyled.iconText} text-sm`} />
            </motion.div>
            <motion.span
              className={arClass(
                `font-semibold max-xl:hidden ${getDashLabelColor()}`,
              )}
              whileHover={{ x: 2 }}
            >
              {singleText.dashboard}
            </motion.span>
          </MotionNavLink>
          {/* Profile Link */}
          <MotionNavLink
            onClick={hideMdDropdownVisibility}
            className={({ isActive }) =>
              arClass(
                `flex items-center gap-3 p-2 rounded-xl text-sm font-medium w-full transition-all duration-300 ${
                  isActive
                    ? applySidebarStyle(getColorScheme(0)).containerActive
                    : applySidebarStyle(getColorScheme(0)).containerInactive
                }`,
              )
            }
            to="/profile"
          >
            <motion.div
              className={arClass(
                `w-8 h-8 rounded-lg flex items-center justify-center ${
                  applySidebarStyle(getColorScheme(0)).iconBg
                } ${shouldShowShadow() ? "shadow-lg" : ""}`,
              )}
              variants={iconVariants}
              whileHover="hover"
            >
              <User
                className={`${applySidebarStyle(getColorScheme(0)).iconText} text-sm`}
              />
            </motion.div>
            <motion.span
              className={arClass(
                `font-semibold max-xl:hidden ${applySidebarStyle(getColorScheme(0)).textColor}`,
              )}
              whileHover={{ x: 2 }}
            >
              {singleText.myProfile}
            </motion.span>
          </MotionNavLink>

          {/* Waiting Room Link */}
          <MotionNavLink
            onClick={hideMdDropdownVisibility}
            className={({ isActive }) =>
              arClass(
                `flex items-center gap-3 p-2 rounded-xl text-sm font-medium w-full transition-all duration-300 ${
                  isActive
                    ? applySidebarStyle(getColorScheme(1)).containerActive
                    : applySidebarStyle(getColorScheme(1)).containerInactive
                }`,
              )
            }
            to="/waiting-room"
          >
            <motion.div
              className={arClass(
                `w-8 h-8 rounded-lg flex items-center justify-center ${
                  applySidebarStyle(getColorScheme(1)).iconBg
                } ${shouldShowShadow() ? "shadow-lg" : ""}`,
              )}
              variants={iconVariants}
              whileHover="hover"
            >
              <Hospital
                className={`${applySidebarStyle(getColorScheme(1)).iconText} text-sm`}
              />
            </motion.div>
            <motion.span
              className={arClass(
                `font-semibold max-xl:hidden ${applySidebarStyle(getColorScheme(1)).textColor}`,
              )}
              whileHover={{ x: 2 }}
            >
              {singleText.waitingRoom}
            </motion.span>
          </MotionNavLink>
          {/* Reception Link */}
          <MotionNavLink
            onClick={hideMdDropdownVisibility}
            className={({ isActive }) =>
              arClass(
                `flex items-center gap-3 p-2 rounded-xl text-sm font-medium w-full transition-all duration-300 ${
                  isActive
                    ? applySidebarStyle(getColorScheme(2)).containerActive
                    : applySidebarStyle(getColorScheme(2)).containerInactive
                }`,
              )
            }
            to="/reception"
          >
            <motion.div
              className={arClass(
                `w-8 h-8 rounded-lg flex items-center justify-center ${
                  applySidebarStyle(getColorScheme(2)).iconBg
                } ${shouldShowShadow() ? "shadow-lg" : ""}`,
              )}
              variants={iconVariants}
              whileHover="hover"
            >
              <ClipboardList
                className={`${applySidebarStyle(getColorScheme(2)).iconText} text-sm`}
              />
            </motion.div>
            <motion.span
              className={arClass(
                `font-semibold max-xl:hidden ${applySidebarStyle(getColorScheme(2)).textColor}`,
              )}
              whileHover={{ x: 2 }}
            >
              {singleText.reception}
            </motion.span>
          </MotionNavLink>
          {/* My Triages Link */}
          <MotionNavLink
            onClick={hideMdDropdownVisibility}
            className={({ isActive }) =>
              arClass(
                `flex items-center gap-3 p-2 rounded-xl text-sm font-medium w-full transition-all duration-300 ${
                  isActive
                    ? applySidebarStyle(getColorScheme(3)).containerActive
                    : applySidebarStyle(getColorScheme(3)).containerInactive
                }`,
              )
            }
            to="/my-triages"
          >
            <motion.div
              className={arClass(
                `w-8 h-8 rounded-lg flex items-center justify-center ${
                  applySidebarStyle(getColorScheme(3)).iconBg
                } ${shouldShowShadow() ? "shadow-lg" : ""}`,
              )}
              variants={iconVariants}
              whileHover="hover"
            >
              <FileHeart
                className={`${applySidebarStyle(getColorScheme(3)).iconText} text-sm`}
              />
            </motion.div>
            <motion.span
              className={arClass(
                `font-semibold max-xl:hidden ${applySidebarStyle(getColorScheme(3)).textColor}`,
              )}
              whileHover={{ x: 2 }}
            >
              {singleText.myTriages}
            </motion.span>
          </MotionNavLink>

          {
            // Clinic Triages Link - only for admins or doctors
            (isAdminRole(roles) || isDoctorRole) && (
              <>
                <MotionNavLink
                  onClick={hideMdDropdownVisibility}
                  className={({ isActive }) =>
                    arClass(
                      `flex items-center gap-3 p-2 rounded-xl text-sm font-medium w-full transition-all duration-300 ${
                        isActive
                          ? applySidebarStyle(getColorScheme(8)).containerActive
                          : applySidebarStyle(getColorScheme(8))
                              .containerInactive
                      }`,
                    )
                  }
                  to="/my-visits"
                >
                  <motion.div
                    className={arClass(
                      `w-8 h-8 rounded-lg flex items-center justify-center ${
                        applySidebarStyle(getColorScheme(8)).iconBg
                      } ${shouldShowShadow() ? "shadow-lg" : ""}`,
                    )}
                    variants={iconVariants}
                    whileHover="hover"
                  >
                    <Heart
                      className={`${applySidebarStyle(getColorScheme(8)).iconText} text-sm`}
                    />
                  </motion.div>
                  <motion.span
                    className={arClass(
                      `font-semibold max-xl:hidden ${applySidebarStyle(getColorScheme(8)).textColor}`,
                    )}
                    whileHover={{ x: 2 }}
                  >
                    {singleText.myVisits}
                  </motion.span>
                </MotionNavLink>
                <MotionNavLink
                  onClick={hideMdDropdownVisibility}
                  className={({ isActive }) =>
                    arClass(
                      `flex items-center gap-3 p-2 rounded-xl text-sm font-medium w-full transition-all duration-300 ${
                        isActive
                          ? applySidebarStyle(getColorScheme(9)).containerActive
                          : applySidebarStyle(getColorScheme(9))
                              .containerInactive
                      }`,
                    )
                  }
                  to="/clinic-visits"
                >
                  <motion.div
                    className={arClass(
                      `w-8 h-8 rounded-lg flex items-center justify-center ${
                        applySidebarStyle(getColorScheme(9)).iconBg
                      } ${shouldShowShadow() ? "shadow-lg" : ""}`,
                    )}
                    variants={iconVariants}
                    whileHover="hover"
                  >
                    <HeartPlus
                      className={`${applySidebarStyle(getColorScheme(9)).iconText} text-sm`}
                    />
                  </motion.div>
                  <motion.span
                    className={arClass(
                      `font-semibold max-xl:hidden ${applySidebarStyle(getColorScheme(9)).textColor}`,
                    )}
                    whileHover={{ x: 2 }}
                  >
                    {singleText.clinicVisits}
                  </motion.span>
                </MotionNavLink>
                <MotionNavLink
                  onClick={hideMdDropdownVisibility}
                  className={({ isActive }) =>
                    arClass(
                      `flex items-center gap-3 p-2 rounded-xl text-sm font-medium w-full transition-all duration-300 ${
                        isActive
                          ? applySidebarStyle(getColorScheme(4)).containerActive
                          : applySidebarStyle(getColorScheme(4))
                              .containerInactive
                      }`,
                    )
                  }
                  to="/clinic-triages"
                >
                  <motion.div
                    className={arClass(
                      `w-8 h-8 rounded-lg flex items-center justify-center ${
                        applySidebarStyle(getColorScheme(4)).iconBg
                      } ${shouldShowShadow() ? "shadow-lg" : ""}`,
                    )}
                    variants={iconVariants}
                    whileHover="hover"
                  >
                    <FolderHeart
                      className={`${applySidebarStyle(getColorScheme(4)).iconText} text-sm`}
                    />
                  </motion.div>
                  <motion.span
                    className={arClass(
                      `font-semibold max-xl:hidden ${applySidebarStyle(getColorScheme(4)).textColor}`,
                    )}
                    whileHover={{ x: 2 }}
                  >
                    {singleText.clinicTriages}
                  </motion.span>
                </MotionNavLink>
              </>
            )
          }

          {
            // Statistics Link - only for admins only
            isAdminRole(roles) && (
              <MotionNavLink
                onClick={hideMdDropdownVisibility}
                className={({ isActive }) =>
                  arClass(
                    `flex items-center gap-3 p-2 rounded-xl text-sm font-medium w-full transition-all duration-300 ${
                      isActive
                        ? applySidebarStyle(getColorScheme(6)).containerActive
                        : applySidebarStyle(getColorScheme(6)).containerInactive
                    }`,
                  )
                }
                to="/statistics"
              >
                <motion.div
                  className={arClass(
                    `w-8 h-8 rounded-lg flex items-center justify-center ${
                      applySidebarStyle(getColorScheme(6)).iconBg
                    } ${shouldShowShadow() ? "shadow-lg" : ""}`,
                  )}
                  variants={iconVariants}
                  whileHover="hover"
                >
                  <BarChart3
                    className={`${applySidebarStyle(getColorScheme(6)).iconText} text-sm`}
                  />
                </motion.div>

                <motion.span
                  className={arClass(
                    `font-semibold max-xl:hidden ${applySidebarStyle(getColorScheme(6)).textColor}`,
                  )}
                  whileHover={{ x: 2 }}
                >
                  {singleText.statistics}
                </motion.span>
              </MotionNavLink>
            )
          }
          {isAdminRole(roles) && (
            <>
              {/* Team Link (Conditional) */}
              <MotionNavLink
                onClick={hideMdDropdownVisibility}
                className={({ isActive }) =>
                  arClass(
                    `flex items-center gap-3 p-2 rounded-xl text-sm font-medium w-full transition-all duration-300 ${
                      isActive
                        ? applySidebarStyle(getColorScheme(5)).containerActive
                        : applySidebarStyle(getColorScheme(5)).containerInactive
                    }`,
                  )
                }
                to="/team"
              >
                <motion.div
                  className={arClass(
                    `w-8 h-8 rounded-lg flex items-center justify-center ${
                      applySidebarStyle(getColorScheme(5)).iconBg
                    } ${shouldShowShadow() ? "shadow-lg" : ""}`,
                  )}
                  variants={iconVariants}
                  whileHover="hover"
                >
                  <Users
                    className={`${applySidebarStyle(getColorScheme(5)).iconText} text-sm`}
                  />
                </motion.div>
                <motion.span
                  className={arClass(
                    `font-semibold max-xl:hidden ${applySidebarStyle(getColorScheme(5)).textColor}`,
                  )}
                  whileHover={{ x: 2 }}
                >
                  {singleText.team}
                </motion.span>
              </MotionNavLink>

              {/* clinic tools and equipment */}
              <MotionNavLink
                onClick={hideMdDropdownVisibility}
                className={({ isActive }) =>
                  arClass(
                    `flex items-center gap-3 p-2 rounded-xl text-sm font-medium w-full transition-all duration-300 ${
                      isActive
                        ? applySidebarStyle(getColorScheme(7)).containerActive
                        : applySidebarStyle(getColorScheme(7)).containerInactive
                    }`,
                  )
                }
                to="/tools-equipment"
              >
                <motion.div
                  className={arClass(
                    `w-8 h-8 rounded-lg flex items-center justify-center ${
                      applySidebarStyle(getColorScheme(7)).iconBg
                    } ${shouldShowShadow() ? "shadow-lg" : ""}`,
                  )}
                  variants={iconVariants}
                  whileHover="hover"
                >
                  <Package
                    className={`${applySidebarStyle(getColorScheme(7)).iconText} text-sm`}
                  />
                </motion.div>
                <motion.span
                  className={arClass(
                    `font-semibold max-xl:hidden ${applySidebarStyle(getColorScheme(7)).textColor}`,
                  )}
                  whileHover={{ x: 2 }}
                >
                  {singleText.toolsAndEquipment}
                </motion.span>
              </MotionNavLink>

              {/* materials link - only for admins or those with permissions */}
              <MotionNavLink
                onClick={hideMdDropdownVisibility}
                className={({ isActive }) =>
                  arClass(
                    `flex items-center gap-3 p-2 rounded-xl text-sm font-medium w-full transition-all duration-300 ${
                      isActive
                        ? applySidebarStyle(getColorScheme(8)).containerActive
                        : applySidebarStyle(getColorScheme(8)).containerInactive
                    }`,
                  )
                }
                to="/materials"
              >
                <motion.div
                  className={arClass(
                    `w-8 h-8 rounded-lg flex items-center justify-center ${
                      applySidebarStyle(getColorScheme(8)).iconBg
                    } ${shouldShowShadow() ? "shadow-lg" : ""}`,
                  )}
                  variants={iconVariants}
                  whileHover="hover"
                >
                  <Syringe
                    className={`${applySidebarStyle(getColorScheme(8)).iconText} text-sm`}
                  />
                </motion.div>
                <motion.span
                  className={arClass(
                    `font-semibold max-xl:hidden ${applySidebarStyle(getColorScheme(8)).textColor}`,
                  )}
                  whileHover={{ x: 2 }}
                >
                  {singleText.materials}
                </motion.span>
              </MotionNavLink>
            </>
          )}

          {/* medicaments link - only for admins or those with permissions */}
          <MotionNavLink
            onClick={hideMdDropdownVisibility}
            className={({ isActive }) =>
              arClass(
                `flex items-center gap-3 p-2 rounded-xl text-sm font-medium w-full transition-all duration-300 ${
                  isActive
                    ? applySidebarStyle(getColorScheme(9)).containerActive
                    : applySidebarStyle(getColorScheme(9)).containerInactive
                }`,
              )
            }
            to="/medicaments"
          >
            <motion.div
              className={arClass(
                `w-8 h-8 rounded-lg flex items-center justify-center ${
                  applySidebarStyle(getColorScheme(9)).iconBg
                } ${shouldShowShadow() ? "shadow-lg" : ""}`,
              )}
              variants={iconVariants}
              whileHover="hover"
            >
              <Pill
                className={`${applySidebarStyle(getColorScheme(9)).iconText} text-sm`}
              />
            </motion.div>
            <motion.span
              className={arClass(
                `font-semibold max-xl:hidden ${applySidebarStyle(getColorScheme(9)).textColor}`,
              )}
              whileHover={{ x: 2 }}
            >
              {singleText.medicaments}
            </motion.span>
          </MotionNavLink>
        </div>

        {/* Dropdown sections with animation and permissions */}
        <motion.div
          className={arClass(
            `flex flex-col gap-3 px-1 py-1 pr-4 max-xl:pr-0  max-xl:mx-0 max-xl:items-center `,
          )}
        >
          {/* Dropdown Links */}
          <AnimatePresence>
            {Object.entries(dropdownLinks || {}).map(([key, group], idx) => {
              const ddIndex = idx;

              return (
                <motion.div
                  key={key}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className={arClass("flex flex-col gap-2")}
                >
                  <DropDown
                    index={ddIndex}
                    links={group.items || []}
                    dropDownName={group.mainName || key}
                    accessKeys={group.accessKeys || []}
                  />
                </motion.div>
              );
            })}
          </AnimatePresence>

          <MotionNavLink
            onClick={hideMdDropdownVisibility}
            className={({ isActive }) =>
              arClass(
                `flex items-center gap-3 p-2 rounded-xl text-sm font-medium w-full transition-all duration-300  ${
                  isActive
                    ? applySidebarStyle(getColorScheme(7)).containerActive
                    : applySidebarStyle(getColorScheme(7)).containerInactive
                }`,
              )
            }
            to="/settings"
          >
            <motion.div
              className={arClass(
                `w-8 h-8 rounded-lg flex items-center justify-center ${
                  applySidebarStyle(getColorScheme(7)).iconBg
                } ${shouldShowShadow() ? "shadow-lg" : ""}`,
              )}
              variants={iconVariants}
              whileHover="hover"
            >
              <Settings
                className={`${applySidebarStyle(getColorScheme(7)).iconText} text-sm`}
              />
            </motion.div>
            <motion.span
              className={arClass(
                `font-semibold max-xl:hidden ${applySidebarStyle(getColorScheme(7)).textColor}`,
              )}
              whileHover={{ x: 2 }}
            >
              {singleText.settings}
            </motion.span>
          </MotionNavLink>
        </motion.div>
      </div>
    </motion.nav>
  );
}
